import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Lock, ShieldCheck, UserCheck } from "lucide-react";

const Icons = { ArrowRight, Eye, Lock, ShieldCheck, UserCheck };

const PILLARS = [
  { icon: Icons.UserCheck, title: "Human oversight", desc: "Professionals remain responsible for interpretation and decisions. PsyTech supports — it does not replace." },
  { icon: Icons.ShieldCheck, title: "Safety boundaries", desc: "The platform refuses diagnosis, medication advice, and emergency guidance by design — and directs users to appropriate support." },
  { icon: Icons.Lock, title: "Privacy-conscious data", desc: "Data minimization, role-based access, secure handling. Sensitive information is treated with care throughout." },
  { icon: Icons.Eye, title: "Transparent limitations", desc: "We're explicit about what PsyTech is and what it isn't — including the fact that it is not a certified medical device." },
];

export default function ResponsibleByDesignSection() {
  return (
    <section id="responsible" data-testid="responsible-section" className="py-20 md:py-24 bg-slate-900 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-600/10 rounded-full blur-3xl" />
      </div>
      <div className="absolute inset-0 bg-[linear-gradient(rgba(14,116,144,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(14,116,144,0.05)_1px,transparent_1px)] bg-[size:60px_60px] pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-12">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.18em] text-cyan-400 mb-3">
            Responsible by design
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Plus_Jakarta_Sans'] leading-tight">
            Built with safety, privacy, and human oversight at the centre
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-300 leading-relaxed">
            PsyTech is designed to support qualified professionals. Every AI-assisted output is meant to be reviewed, questioned, and validated — not accepted as a final answer.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 hover:border-cyan-500/40 transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-cyan-500/10 ring-1 ring-cyan-500/30 flex items-center justify-center mb-4">
                <p.icon className="w-5 h-5 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2 font-['Plus_Jakarta_Sans']">{p.title}</h3>
              <p className="text-sm text-slate-300 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to="/trust-safety"
            className="inline-flex items-center gap-2 rounded-full bg-cyan-600 hover:bg-cyan-500 text-white px-6 py-3 text-sm font-semibold transition-all shadow-lg shadow-cyan-900/30"
            data-testid="responsible-trust-safety-link"
          >
            Read Trust &amp; Safety <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/privacy"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 hover:bg-white/10 text-white px-6 py-3 text-sm font-semibold transition-all backdrop-blur"
            data-testid="responsible-privacy-link"
          >
            See our privacy approach
          </Link>
        </div>
      </div>
    </section>
  );
}
